import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { formatDate } from '../../../_common/core/validation'
import { getSheduledInvitations, deleteSheduledInvitation } from '../../../_common/core/http_services'
import { ScheduledEmails } from '../../_features/messaging/ScheduledEmails'
import Card from '../../_library/Card'
import LoadingBar from '../../_library/LoadingBar'
import ConfirmModal from '../../_library/ConfirmModal'
import { showAxiosError } from '../../utils/messenger'
import { getTableColumns } from '../../utils/sortableTableUtils'
import { DISPLAY_FORMAT } from '../../constants/timeFormats'

const scheduledInvitationsColumns = getTableColumns([
  { key: 'full_name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'invited_by_full_name', label: 'Invited By' },
  { key: 'ticketType', label: 'Ticket Type' },
  { key: 'maxQuantity', label: 'Max Quantity' },
  {
    label: 'Scheduled At',
    key: 'schedule_date',
    normalizer: value => formatDate(value, DISPLAY_FORMAT),
  },
])

const AUTO_REFRESH_TIME = 10000

const ScheduledInvitations = ({ event }) => {
  const [scheduledInvitations, setScheduledInvitations] = useState({
    data: [],
    isLoading: false,
  })
  const [deletingScheduledInvitation, setDeletingScheduledInvitation] = useState({
    id: null,
    isOpen: false,
  })

  const scheduledInvitationsActions = useMemo(
    () => [
      {
        label: 'Delete',
        className: 'btn btn-danger',
        icon: 'fa fa-trash fa-fw',
        onClick: data =>
          setDeletingScheduledInvitation({
            id: data.id,
            isOpen: true,
          }),
      },
    ],
    [],
  )

  const fetchScheduledInvitations = useCallback(async () => {
    setScheduledInvitations(prevState => ({
      ...prevState,
      isLoading: true,
    }))

    try {
      const scheduledInvitationsData = await getSheduledInvitations(event.id)
      setScheduledInvitations({
        data: scheduledInvitationsData,
        isLoading: false,
      })
    } catch (err) {
      const errMsg = err?.errors[0].details
      showAxiosError(err?.errors[0], errMsg, 3)
      setScheduledInvitations(prevState => ({
        ...prevState,
        isLoading: false,
      }))
    }
  }, [event.id])

  const handleDeleteScheduledMessage = async id => {
    try {
      setScheduledInvitations(prevState => ({
        ...prevState,
        isLoading: true,
      }))

      await deleteSheduledInvitation(id)
      fetchScheduledInvitations()
    } catch (err) {
      const errMsg = err?.errors[0].details
      showAxiosError(err?.errors[0], errMsg, 3)

      setScheduledInvitations(prevState => ({
        ...prevState,
        isLoading: false,
      }))
    }
  }

  const handleDeleteScheduledInvitationModalResponse = response => {
    switch (response) {
      case 'confirm': {
        const { id } = deletingScheduledInvitation

        setDeletingScheduledInvitation({
          id: null,
          isOpen: false,
        })

        handleDeleteScheduledMessage(id)
        break
      }
      case 'cancel':
        setDeletingScheduledInvitation({
          id: null,
          isOpen: false,
        })
        break
      default:
        break
    }
  }

  useEffect(() => {
    fetchScheduledInvitations()

    // Auto-refresh every 10 seconds
    const shedueledInvitationsInterval = setInterval(fetchScheduledInvitations, AUTO_REFRESH_TIME)

    // Cleanup the interval on component unmount
    return () => clearInterval(shedueledInvitationsInterval)
  }, [fetchScheduledInvitations])

  return (
    <Card title="Scheduled invitations">
      {scheduledInvitations.isLoading ? (
        <LoadingBar title="Hold tight! We're getting your scheduled messages..." />
      ) : (
        <ScheduledEmails
          columns={scheduledInvitationsColumns}
          data={scheduledInvitations.data}
          actions={scheduledInvitationsActions}
        />
      )}
      {deletingScheduledInvitation.isOpen && (
        <ConfirmModal
          isOpen={deletingScheduledInvitation.isOpen}
          header="Warning"
          content="Are you sure you want to delete the scheduled invitation?"
          actions={[
            {
              value: 'cancel',
              label: 'Cancel',
              className: 'btn btn-cancel',
            },
            {
              value: 'confirm',
              label: 'Confirm',
              className: 'btn btn-danger',
            },
          ]}
          onAction={handleDeleteScheduledInvitationModalResponse}
        />
      )}
    </Card>
  )
}

export default ScheduledInvitations
